import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
        setMobileCatOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-background-50 shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-18">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <span className="text-lg md:text-xl font-heading font-semibold text-foreground-900 tracking-tight">
                kakobuy<span className="text-primary-500">finds</span>.fit
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <Link
                to="/"
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-4-line text-base"></i>
                {t('nav.home')}
              </Link>

              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-grid-line text-base"></i>
                  {t('nav.categories')}
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-background-50 rounded-xl py-3 px-2 min-w-[240px] shadow-lg border border-background-200/70 z-50">
                    <div className="grid grid-cols-1">
                      {categories.map((cat) => (
                        <a
                          key={cat.id}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                            <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                          </div>
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/artykul/kakobuy-spreadsheet-kompletny-przewodnik"
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line text-base"></i>
                {t('nav.shoppingGuide')}
              </Link>

              <a
                href="#faq"
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-question-line text-base"></i>
                {t('nav.faq')}
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-foreground-900 text-background-50 text-sm font-medium px-5 py-2.5 rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
              >
                {t('hero.buyNow')}
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer relative z-50"
              aria-label="Menu"
            >
              <i className={`text-2xl text-foreground-900 transition-transform duration-200 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-foreground-900/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          ></div>
          <div
            ref={mobileRef}
            className="absolute top-0 right-0 w-[85%] max-w-[380px] h-full bg-background-50 shadow-2xl overflow-y-auto"
          >
            <div className="pt-20 px-5 pb-8">
              <div className="space-y-1 mb-8">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3.5 text-base font-medium text-foreground-900 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
                >
                  <span className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-home-4-line text-primary-500 text-lg"></i>
                  </span>
                  {t('nav.home')}
                </Link>

                <Link
                  to="/artykul/kakobuy-spreadsheet-kompletny-przewodnik"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3.5 text-base font-medium text-foreground-900 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
                >
                  <span className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-book-open-line text-secondary-500 text-lg"></i>
                  </span>
                  {t('nav.shoppingGuide')}
                </Link>

                <button
                  onClick={() => setMobileCatOpen(!mobileCatOpen)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-base font-medium text-foreground-900 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-grid-line text-accent-500 text-lg"></i>
                    </span>
                    {t('nav.categories')}
                  </span>
                  <i className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {mobileCatOpen && (
                  <div className="ml-4 pl-4 border-l-2 border-background-200/70 mt-1 space-y-0.5">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                        onClick={() => setMobileOpen(false)}
                      >
                        <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0">
                          <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                        </div>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}

                <a
                  href="#faq"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3.5 text-base font-medium text-foreground-900 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
                >
                  <span className="w-9 h-9 rounded-lg bg-background-200/70 flex items-center justify-center flex-shrink-0">
                    <i className="ri-question-line text-foreground-600 text-lg"></i>
                  </span>
                  {t('nav.faq')}
                </a>
              </div>

              <div className="border-t border-background-200/70 pt-6 space-y-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 bg-primary-500 text-background-50 text-base font-semibold px-6 py-3.5 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  {t('nav.buyNow')}
                  <i className="ri-arrow-right-up-line text-lg"></i>
                </a>
                <Link
                  to="/artykul/kakobuy-spreadsheet-kompletny-przewodnik"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 bg-secondary-100 text-secondary-800 text-sm font-medium px-6 py-3 rounded-full hover:bg-secondary-200 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-open-line"></i>
                  {t('nav.shoppingGuide')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}